// MonteCarloSection.tsx — the headline "tax-aware Monte Carlo" feature: toggle + inputs
// (volatility, path count, "Neu würfeln" reroll) plus the resulting fan chart and honest
// summary stats. Lives inside ResultsPanel, forecast mode only (see its own guard) — the
// MC endpoint reuses the EXACT ForecastConfig the deterministic Verlauf chart above it was
// computed from (RunResult.config), so the two never silently disagree.
import { useEffect, useState } from 'preact/hooks';
import type { ForecastConfig, MonteCarloResult } from '@etf-cockpit/engine';
import { ApiError, postMonteCarlo } from '../lib/api.js';
import { percentToBp } from '../lib/convert.js';
import { buildMonteCarloRequestBody, nextSeed } from '../lib/montecarlo.js';
import type { MonteCarloPathCount, MonteCarloSettings } from '../lib/montecarlo.js';
import { deflateMonteCarloResult } from '../lib/realTerms.js';
import type { RealTermsSettings } from '../lib/realTerms.js';
import { NumberField } from './NumberField.js';
import { ToggleField } from './ToggleField.js';
import { MonteCarloChart } from './MonteCarloChart.js';
import { useExplain } from './Hint.js';
import { formatBp, formatCents, formatDateDe } from '../lib/format.js';

const PATH_OPTIONS: MonteCarloPathCount[] = [250, 500, 1000];

export interface MonteCarloSectionProps {
  forecastConfig: ForecastConfig;
  settings: MonteCarloSettings;
  onSettingsChange: (settings: MonteCarloSettings) => void;
  /** Optional: when set and enabled, the fan chart + stats below are displayed deflated
   * to today's purchasing power (see lib/realTerms.ts) — same toggle as the rest of the
   * results view (ResultsPanel), not a separate control here. */
  realTerms?: RealTermsSettings | undefined;
}

export function MonteCarloSection({ forecastConfig, settings, onSettingsChange, realTerms }: MonteCarloSectionProps) {
  const showExplanations = useExplain();
  const [enabled, setEnabled] = useState(false);
  const [result, setResult] = useState<MonteCarloResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    postMonteCarlo(buildMonteCarloRequestBody(forecastConfig, settings))
      .then((res) => {
        if (cancelled) return;
        setResult(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Unbekannter Fehler bei der Monte-Carlo-Simulation.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, forecastConfig, settings.volatilityPercent, settings.paths, settings.seed]);

  /** Eagerly flips into the loading state on any input change, so re-fetches never leave
   * a stale chart sitting there with no indication a recompute is in flight (the effect
   * above would also set loading=true, just one tick later — this avoids that flash and
   * also clears a previous error immediately). */
  function updateSettings(next: MonteCarloSettings): void {
    if (enabled) {
      setLoading(true);
      setError(null);
    }
    onSettingsChange(next);
  }

  function handleToggle(next: boolean): void {
    setEnabled(next);
    if (next) {
      setLoading(true);
      setError(null);
      setResult(null);
    }
  }

  const hasWithdrawal = forecastConfig.withdrawal !== undefined;
  const inflationBp = realTerms?.enabled ? percentToBp(realTerms.inflationPercent) : 0;
  const displayResult = result ? deflateMonteCarloResult(result, inflationBp) : null;

  return (
    <div class="card">
      <h2>
        Monte-Carlo / Renditeband
        {realTerms?.enabled ? <span class="badge-real-terms">in heutiger Kaufkraft</span> : null}
      </h2>
      <ToggleField
        label="Monte-Carlo-Simulation anzeigen"
        checked={enabled}
        onChange={handleToggle}
        hint={
          showExplanations
            ? 'Simuliert viele zufällige Renditepfade (geometrische brownsche Bewegung) durch dieselbe Steuer-Engine wie die Prognose oben — inkl. AGE/KESt/Entnahmeplan auf jedem einzelnen Pfad, nicht nur auf einer gemittelten Durchschnittsrendite.'
            : undefined
        }
      />

      {!enabled ? null : (
        <>
          <NumberField
            label="Volatilität σ"
            unit="% p.a."
            value={settings.volatilityPercent}
            onChange={(v) => updateSettings({ ...settings, volatilityPercent: v })}
            min={0}
            max={40}
            step={1}
          />

          <div class="field">
            <span class="field-label">Pfade</span>
            <div class="segmented" role="group" aria-label="Anzahl simulierter Pfade">
              {PATH_OPTIONS.map((p) => (
                <button
                  key={p}
                  type="button"
                  class="chip"
                  aria-pressed={settings.paths === p}
                  onClick={() => updateSettings({ ...settings, paths: p })}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div class="btn-row" style={{ marginBottom: '0.6rem' }}>
            <button
              type="button"
              class="btn"
              disabled={loading}
              onClick={() => updateSettings({ ...settings, seed: nextSeed(settings.seed) })}
            >
              Neu würfeln
            </button>
          </div>

          {loading ? (
            <div class="empty-state">
              <span class="spinner" /> Wird berechnet…
            </div>
          ) : error ? (
            <div class="banner banner-error" role="alert">
              <strong>Fehler</strong>
              <span>{error}</span>
            </div>
          ) : !displayResult ? (
            <div class="empty-state">Noch keine Simulation.</div>
          ) : (
            <>
              <MonteCarloChart band={displayResult.band} />

              <div class="summary-grid" style={{ marginTop: '0.6rem' }}>
                <Tile label="Median-Endvermögen" value={formatCents(displayResult.finalStats.p50)} />
                <Tile
                  label="p10–p90-Spanne"
                  value={`${formatCents(displayResult.finalStats.p10)} – ${formatCents(displayResult.finalStats.p90)}`}
                />
                <Tile
                  label="Wahrscheinlichkeit Verlust"
                  value={formatBp(Math.round(displayResult.finalStats.probLossVsContributed * 10_000))}
                />
                {hasWithdrawal && displayResult.finalStats.probDepleted !== undefined ? (
                  <>
                    <Tile
                      label="Wahrscheinlichkeit Depot vorzeitig leer"
                      value={formatBp(Math.round(displayResult.finalStats.probDepleted * 10_000))}
                    />
                    <div class="summary-tile">
                      <div class="label">Median Depot-leer-Datum</div>
                      <div class="value" style={{ fontSize: '0.85rem' }}>
                        {displayResult.finalStats.medianDepletionDate
                          ? formatDateDe(displayResult.finalStats.medianDepletionDate)
                          : 'Kein Pfad leer'}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
              <p class="field-hint">
                {displayResult.echo.paths} Pfade, σ = {settings.volatilityPercent} % p.a., Seed {displayResult.echo.seed}.
                Alle Werte direkt aus der Simulation berechnet — keine erfundenen Zahlen.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div class="summary-tile">
      <div class="label">{label}</div>
      <div class="value">{value}</div>
    </div>
  );
}
